import React, { useMemo } from 'react'
import PropTypes from 'prop-types'

import { Box, Grid, NoSsr, Typography } from '@material-ui/core'
import { makeStyles, useTheme } from '@material-ui/core/styles'
import useMediaQuery from '@material-ui/core/useMediaQuery'
import { detect } from 'detect-browser'

import useAutoplayHeroVideo from 'common/hooks/useAutoplayHeroVideo'
import { formatPrice } from 'common/utils/format'
import Image from 'artkive/components/Image'
import ContainedSection from 'artkive/components/PageSections/ContainedSection'
import HowItWorks from 'artkive/components/PageSections/HowItWorks'
import ProductDescriptionItem from 'artkive/components/PageSections/ProductDescriptionItem'
import ProductPriceTypography from 'artkive/components/PageSections/ProductPriceTypography'
import GetStartedWithBox from 'artkive/components/sections/GetStartedWithBox'
import Testimonial from 'artkive/components/sections/Testimonial'
import YouWillAlsoLoveCarousel, {
  BOOK_SLIDE,
  getMosaicSlide,
} from 'artkive/components/sections/YouWillAlsoLoveCarousel'
import { TrackButton } from 'artkive/components/Tracker'
import usePageScrollTracker from 'artkive/components/Tracker/usePageScrollTracker'
import VirtualGalleryCountdownBox from 'artkive/components/VirtualGalleryCountdownBox'
import VirtualGalleryPrice from 'artkive/components/VirtualGalleryPrice'
import { GET_BOX_CLICK } from 'artkive/constants/tracker/mainNavigation'
import withApp from 'artkive/hoc/withApp'
import useResponsive from 'artkive/hooks/useResponsive'
import useSale from 'artkive/hooks/useSale'
import { PRICING } from 'artkive/utils/constants'
import theme from 'artkive/utils/theme'

import BrushStrokes from 'images/ecom/brand/brushStrokes.svg'
import Bubbles from 'images/ecom/bubbles.svg'
import LimitedTimeOfferBadge from 'images/ecom/LimitedTimeOfferBadge.svg'
import About1 from 'images/ecom/virtual-gallery/GalleryFocus1.jpg'
import About2 from 'images/ecom/virtual-gallery/GalleryFocus2.jpg'
import About3 from 'images/ecom/virtual-gallery/GalleryFocus3.jpg'
import TestimonialImage from 'images/ecom/virtual-gallery/Testimonial.jpg'
import VideoPosterDesktop from 'images/ecom/virtual-gallery/video-poster/poster.jpg'
import VideoPosterMobile from 'images/ecom/virtual-gallery/video-poster/poster-mobile.jpg'
import VideoPosterTablet from 'images/ecom/virtual-gallery/video-poster/poster-tablet.jpg'

import ProductDescriptionSection from '../GetStarted/ProductDescriptionSection'

import { howItWorksSteps, imageBgPosition, testimonialUser, virtualGalleryBullets } from './VirtualGallery.constants'
import {
  AboutImageWrapper,
  BadgeWrapper,
  CountdownBox,
  DescriptionWrapper,
  GalleryTheirOwnWrapper,
  GridContainer,
  ImagePlaceholder,
  ImageWrapper,
  LimitedTextBox,
  LimitedTimeText,
  PriceInfoBox,
  Video,
  VideoWrapper,
} from './VirtualGallery.style'

const useStyles = makeStyles((theme) => ({
  root: {
    minHeight: '100vh',
    overflowX: 'hidden',
    backgroundColor: theme.colors.sky[100],
  },
  description: {
    [theme.breakpoints.up('xl')]: {
      maxWidth: '640px',
    },
  },
}))

const VIDEO_TAG_ID = 'virtual-gallery-video'

const browser = detect()

const GetMyBoxButton = ({ href, title, trackerSection }) => (
  <TrackButton
    color='primary'
    size='large'
    variant='contained'
    href={href}
    trackerEvent={GET_BOX_CLICK}
    trackerOptions={{
      section: trackerSection,
    }}
  >
    {title || 'Get My Box'}
  </TrackButton>
)

GetMyBoxButton.propTypes = {
  href: PropTypes.string,
  title: PropTypes.string,
  trackerSection: PropTypes.string.isRequired,
}

const VirtualGallery = ({ mosaic_pricing }) => {
  usePageScrollTracker()
  const classes = useStyles()

  const { boxCheckoutUrl } = useSale()

  const muiTheme = useTheme()
  const { isMobile } = useResponsive()

  const tabletBreakpoint = muiTheme.breakpoints.values.md // 960
  const mobileBreakpoint = muiTheme.breakpoints.values.sm // 600
  const isDesktop = useMediaQuery(muiTheme.breakpoints.up(tabletBreakpoint))
  const isTablet = useMediaQuery(muiTheme.breakpoints.between(mobileBreakpoint, tabletBreakpoint))

  const isSafariBrowser = browser?.name === 'safari' && browser?.os === 'Mac OS'

  const slides = useMemo(() => [BOOK_SLIDE, getMosaicSlide(mosaic_pricing)], [mosaic_pricing])

  useAutoplayHeroVideo({ id: VIDEO_TAG_ID })

  return (
    <div className={classes.root}>
      <ProductDescriptionSection
        video={
          <VideoWrapper>
            <ImagePlaceholder />
            <NoSsr>
              <Video
                id={VIDEO_TAG_ID}
                autoPlay
                muted
                loop
                playsInline
                preload='auto'
                poster={isDesktop ? VideoPosterDesktop : isTablet ? VideoPosterTablet : VideoPosterMobile}
              >
                {isDesktop && !isSafariBrowser && (
                  <source
                    src='https://artkive-public-assets.s3.us-east-1.amazonaws.com/videos/gallery-video-1080p.webm'
                    type='video/webm'
                    media={`(min-width: ${tabletBreakpoint}px)`}
                  />
                )}
                <source
                  src='https://artkive-public-assets.s3.us-east-1.amazonaws.com/videos/gallery-video-1080p.mp4'
                  type='video/mp4'
                  media={`(min-width: ${mobileBreakpoint}px)`}
                />
                <source
                  src='https://artkive-public-assets.s3.us-east-1.amazonaws.com/videos/gallery-video-720p.mp4'
                  type='video/mp4'
                />
              </Video>
            </NoSsr>
          </VideoWrapper>
        }
        imageStyle='contain'
        productBg={theme.colors.white.main}
        descriptionBg={theme.colors.bg.yellow}
        descriptionClassName={classes.description}
        withBubbles
        overImage={
          <BadgeWrapper>
            <Image src={LimitedTimeOfferBadge} alt='Limited Time Offer' />
          </BadgeWrapper>
        }
      >
        <Typography variant='h1' align={isMobile ? 'center' : 'left'}>
          Virtual Gallery
        </Typography>
        <Box mb={3} mt={2}>
          <Typography variant='body1'>
            Bring a smile to your child’s face with their own art gallery. Using the art pieces from your Kids Art Book,
            we will curate a custom gallery walkthrough video that you can share with friends and family.
          </Typography>
        </Box>
        {virtualGalleryBullets.map(({ text }, index) => (
          <ProductDescriptionItem key={index}>{text}</ProductDescriptionItem>
        ))}
        <PriceInfoBox>
          <Typography variant='body1'>
            <strong>
              <ProductPriceTypography>{formatPrice(PRICING.virtualGallery.base, 'short')}</ProductPriceTypography>{' '}
              bundled with Kids Art Book
            </strong>
          </Typography>
          <Typography variant='caption'>*Price doesn’t include Artkive Box</Typography>
        </PriceInfoBox>
        {!isMobile && (
          <LimitedTextBox>
            <LimitedTimeText>Limited Time Offer!</LimitedTimeText>
          </LimitedTextBox>
        )}
        <CountdownBox>
          <VirtualGalleryCountdownBox
            withImage={!isMobile}
            withFreePrice={isMobile}
            title={isMobile ? 'Limited Time Offer!' : <VirtualGalleryPrice />}
          />
        </CountdownBox>
      </ProductDescriptionSection>
      <ContainedSection>
        <Testimonial
          imageSrc={TestimonialImage}
          quote="It was an emotional experience to see my daughter's art in a gallery format. The layout was clean,
                engaging and made it easy to appreciate each piece. This is a great idea because it can be shared with
                teachers and family members."
          user={testimonialUser}
          imageBgPosition={imageBgPosition}
        />
      </ContainedSection>
      <HowItWorks title='How It Works' variant='image' steps={howItWorksSteps}>
        <Box justifyContent='center' display='flex' pt={10}>
          <GetMyBoxButton href={boxCheckoutUrl} trackerSection='how_it_works_section' />
        </Box>
      </HowItWorks>
      <GalleryTheirOwnWrapper disableGutters={isMobile} py={0}>
        <GridContainer container direction={isMobile ? 'column' : 'row'}>
          <Grid item xs md={6}>
            <ImageWrapper />
          </Grid>
          <DescriptionWrapper item xs md={6}>
            <Typography variant='h2' align={isMobile ? 'center' : 'left'}>
              A Gallery of Their Own
            </Typography>
            <Box mb={6} mt={3}>
              <Typography variant='body1' align='left'>
                Enjoy a video walking through a beautiful gallery where your kid's artwork is displayed on the walls,
                just like a museum exhibit. Watch it together, share it with friends and family, and relive the
                memories.
              </Typography>
            </Box>
            <Box display='flex' justifyContent={isMobile ? 'center' : 'left'}>
              <GetMyBoxButton
                href={boxCheckoutUrl}
                title='Get Artkive Box'
                trackerSection='gallery_of_their_own_section'
              />
            </Box>
          </DescriptionWrapper>
        </GridContainer>
      </GalleryTheirOwnWrapper>
      <ContainedSection
        title={
          <Typography variant='h2' align='center'>
            A Whole New Kind Of Gallery
          </Typography>
        }
        bgcolor={theme.colors.white.main}
      >
        <Box position='relative'>
          <Box position='absolute' top='-50px' right='-10px' zIndex={1}>
            <Image src={BrushStrokes} style={{ width: '64px', transform: 'scaleX(-1)' }} />
          </Box>
          <Box position='absolute' bottom='60px' left='-10px' zIndex={0}>
            <Image src={Bubbles} style={{ width: '80px', transform: 'scaleX(-1)' }} />
          </Box>
          <Grid container style={{ position: 'relative', zIndex: '0' }}>
            <Grid xs={12} md={4} item>
              <Box textAlign='center' px={isMobile ? 0 : 4} mb={isMobile ? 6 : 0}>
                <AboutImageWrapper>
                  <Image src={About1} />
                </AboutImageWrapper>
                <Typography variant='h3'>1. 100 works of art from your child’s book</Typography>
              </Box>
            </Grid>
            <Grid xs={12} md={4} item>
              <Box textAlign='center' px={isMobile ? 0 : 4} mb={isMobile ? 6 : 0}>
                <AboutImageWrapper>
                  <Image src={About2} />
                </AboutImageWrapper>
                <Typography variant='h3'>2. Each piece in its own virtual frame</Typography>
              </Box>
            </Grid>
            <Grid xs={12} md={4} item>
              <Box textAlign='center' px={isMobile ? 0 : 4}>
                <AboutImageWrapper>
                  <Image src={About3} />
                </AboutImageWrapper>
                <Typography variant='h3'>3. Easily shareable video format to delight friends and family</Typography>
              </Box>
            </Grid>
          </Grid>
        </Box>
      </ContainedSection>
      <GetStartedWithBox />
      <YouWillAlsoLoveCarousel slides={slides} />
    </div>
  )
}

VirtualGallery.propTypes = {
  mosaic_pricing: PropTypes.shape({
    small: PropTypes.object.isRequired,
    large: PropTypes.object.isRequired,
  }).isRequired,
}

export default withApp(VirtualGallery)
